import React, { useState, useEffect } from "react";
import moment from "moment";
import toast from "react-hot-toast";

import DatePicker from "../../../DatePicker/DatePicker";
import TimePicker from "../../../TimePicker/TimePicker";
import { ReactComponent as InfoIcon } from "../../../../assets/images/icons/info.svg";
import Checkbox from "../../../Checkbox/Checkbox";
import ClubbieButton from "../../../ClubbieButton/ClubbieButton";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import ClubbieModal from "../../../Modal/ClubbieModal";
import {
  MATCH_RULES_OPTIONS,
  MATCH_RULES_TEXTS,
  MATCH_DAYS,
  MATCH_TIMINGS,
  POINTS_SYSTEM_OPTIONS,
  POINTS_SYSTEM_TEXTS,
  usePhaseForm,
} from "../Hooks/usePhaseForm";
import {
  INITIAL_KNOCKOUT_CONFIG,
  INITIAL_GROUP_CONFIG,
  INITIAL_PHASE2_SETTINGS,
} from "../../../../pages/tournament/format/constants";

const TEAMS_COUNT_OPTIONS = [2, 4, 8, 16, 32];
const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const PhaseSettingsModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  phases,
  selectedTournament,
  mode = "create",
  initialData,
  isFirstPhase = false,
  templateType = "manualGrid",
}) => {
  // For create mode, pre-fill from previous phase if one exists
  const previousPhase = !initialData && phases?.length > 0 ? phases[phases.length - 1] : null;
  const source = initialData || previousPhase;

  const baseInitialState = {
    // Pre-fill name: edit mode uses initialData, auto templates get a default name
    name: initialData?.name || ({
      knockoutPhase: "Knockout stage",
      groupPhase: "Group stage",
      groupStageKnockout: "Group stage",
    }[templateType] || ""),
    // Pre-fill general settings from previous phase or initialData
    availablePitches: source?.availablePitches || [],
    // Pre-fill match rules (points system + timing)
    matchRules: {
      ...(source?.matchRules || {}),
    },
    // Pre-fill match schedule
    matchSchedule: {
      startDate:
        initialData?.matchSchedule?.startDate ||
        selectedTournament?.matchSchedule?.startDate ||
        "",
      endDate:
        initialData?.matchSchedule?.endDate ||
        selectedTournament?.matchSchedule?.endDate ||
        "",
      matchDays:
        (source?.matchSchedule?.matchDays?.length > 0 && source.matchSchedule.matchDays) ||
        (selectedTournament?.matchSchedule?.matchDays?.length > 0 && selectedTournament.matchSchedule.matchDays) ||
        ALL_DAYS,
      matchTimings:
        source?.matchSchedule?.matchTimings ||
        selectedTournament?.matchSchedule?.matchTimings ||
        [],
      startTime:
        source?.matchSchedule?.startTime ||
        selectedTournament?.matchSchedule?.startTime ||
        "",
      dayEndTime:
        source?.matchSchedule?.dayEndTime ||
        selectedTournament?.matchSchedule?.dayEndTime ||
        "",
    },
  };

  const {
    formData,
    setFormData,
    resetForm,
    updateField,
    updateNestedField,
    selectedTimes,
    setSelectedTimes,
  } = usePhaseForm(baseInitialState);

  const [currentStep, setCurrentStep] = useState(1);

  // Template-specific state
  const [groupConfig, setGroupConfig] = useState({ ...INITIAL_GROUP_CONFIG });
  const [bracketConfig, setBracketConfig] = useState({ ...INITIAL_KNOCKOUT_CONFIG });
  const [phase2Settings, setPhase2Settings] = useState({ ...INITIAL_PHASE2_SETTINGS });

  // When matchDays changes while "start after previous phase" is on, recalculate start date
  const previousPhaseEndDate = phases?.length > 0
    ? phases[phases.length - 1]?.matchSchedule?.endDate
    : null;

  useEffect(() => {
    if (formData.startAfterPreviousPhase && previousPhaseEndDate) {
      const matchDaysList = formData.matchSchedule.matchDays || [];
      if (matchDaysList.length > 0) {
        let nextDay = moment(previousPhaseEndDate).add(1, "day");
        let daysChecked = 0;
        while (!matchDaysList.includes(nextDay.format("dddd")) && daysChecked < 7) {
          nextDay.add(1, "day");
          daysChecked++;
        }
        const newStart = nextDay.format("YYYY-MM-DD");
        if (newStart !== formData.matchSchedule.startDate) {
          updateNestedField("matchSchedule", "startDate", newStart);
        }
      }
    }
  }, [formData.matchSchedule.matchDays, formData.startAfterPreviousPhase]);

  // Dynamic step labels based on template type
  const getStepLabels = () => {
    const base = ["General settings", "Points System", "Match timing", "Schedule"];
    if (templateType === "knockoutPhase") return [...base, "Bracket Config"];
    if (templateType === "groupPhase") return [...base, "Group Config"];
    if (templateType === "groupStageKnockout") return [...base, "Group Config", "Knockout Stage", "Bracket Config"];
    return base; // manualGrid
  };

  // For group+knockout, show which phase the current step configures
  const getPhaseContext = () => {
    if (templateType !== "groupStageKnockout") return null;
    if (currentStep <= 5) return "Group Stage";
    return "Knockout Stage";
  };
  const phaseContext = getPhaseContext();
  const STEP_LABELS = getStepLabels();
  const totalSteps = STEP_LABELS.length;

  // Competition date bounds for constraining date pickers
  const competitionStartDate = selectedTournament?.matchSchedule?.startDate
    ? new Date(selectedTournament.matchSchedule.startDate).toISOString().split("T")[0]
    : "";
  const competitionEndDate = selectedTournament?.matchSchedule?.endDate
    ? new Date(selectedTournament.matchSchedule.endDate).toISOString().split("T")[0]
    : "";

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    return moment(dateStr).format("MMM D, YYYY");
  };

  const handleClose = () => {
    resetForm();
    setCurrentStep(1);
    setGroupConfig({ ...INITIAL_GROUP_CONFIG });
    setBracketConfig({ ...INITIAL_KNOCKOUT_CONFIG });
    setPhase2Settings({ ...INITIAL_PHASE2_SETTINGS });
    onClose();
  };

  const handleCreate = () => {
    // Validate phase name before submitting
    if (!formData.name || !formData.name.trim()) {
      toast.error("Please enter a stage name");
      return;
    }

    // For group+knockout, validate phase 2 name
    if (templateType === "groupStageKnockout" && !phase2Settings.name?.trim()) {
      toast.error("Please enter a knockout stage name");
      return;
    }

    // Call parent's submit handler with extra config for non-manual templates
    if (templateType === "manualGrid") {
      onSubmit(formData);
    } else {
      onSubmit(formData, {
        templateType,
        groupConfig,
        bracketConfig,
        phase2Settings,
      });
    }
  };

  // Get pitch options from tournament venue (same logic as old Format.js)
  const getPitchOptions = () => {
    if (selectedTournament?.matchVenue?.pitches?.length > 0) {
      return selectedTournament.matchVenue.pitches;
    }

    // Fallback: Generate default pitches based on numberOfPitches
    const numberOfPitches = selectedTournament?.matchVenue?.numberOfPitches || 1;
    return Array.from(
      { length: numberOfPitches },
      (_, i) => `Pitch ${String.fromCharCode(65 + i)}` // 65 is ASCII for 'A'
    );
  };

  const handleAddTime = (e) => {
    const time = e.target.value;
    if (time && !selectedTimes.includes(time)) {
      const newTimes = [...selectedTimes, time];
      setSelectedTimes(newTimes);
      updateNestedField("matchSchedule", "matchTimings", newTimes);
    }
    e.target.value = "";
  };

  const handleRemoveTime = (time) => {
    const newTimes = selectedTimes.filter((t) => t !== time);
    setSelectedTimes(newTimes);
    updateNestedField("matchSchedule", "matchTimings", newTimes);
  };

  // Shared input class
  const inputClass = "w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors";

  const renderStepHeader = () => (
    <div className="mb-6">
      {phaseContext && (
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs font-semibold uppercase  px-3 py-1 rounded-full bg-[#001A2A] text-[#00E7FF]">
            {phaseContext}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
      {STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = currentStep === stepNum;
        const isEditing = mode === "edit";
        const canClick = isEditing || currentStep > idx;
        return (
          <button
            key={label}
            type="button"
            className={`flex px-3 py-2 text-xs items-center justify-center font-medium transition-colors rounded-full ${
              isActive
                ? "bg-[#001A2A] text-[#00E7FF]"
                : canClick
                  ? "text-app-text-muted hover:text-app-accent cursor-pointer"
                  : "text-app-text-muted cursor-not-allowed opacity-50"
            }`}
            onClick={() => {
              if (canClick) setCurrentStep(stepNum);
            }}
            disabled={!canClick && !isActive}
          >
            {label}
          </button>
        );
      })}
      </div>
    </div>
  );

  // Step 1: General settings — Phase name + Available pitches only
  const renderGeneralSettings = () => (
    <div className="space-y-5">
      <div>
        <h5 className="text-base font-medium text-[#FEFCEB] mb-5">General settings</h5>
      </div>
      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Phase name</label>
        <input
          type="text"
          className={inputClass}
          value={formData.name}
          placeholder="Knockout stage"
          onChange={(e) => updateField("name", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Available pitches</label>
        <div className="flex flex-col space-y-2">
          <Checkbox
            checked={getPitchOptions().length > 0 && formData.availablePitches.length === getPitchOptions().length}
            onChange={() => {
              const allSelected = getPitchOptions().length > 0 && formData.availablePitches.length === getPitchOptions().length;
              updateField("availablePitches", allSelected ? [] : getPitchOptions());
            }}
            label="Select All"
          />

          {getPitchOptions().map((pitch) => (
            <Checkbox
              key={pitch}
              checked={formData.availablePitches.includes(pitch)}
              onChange={() => {
                const pitches = formData.availablePitches.includes(pitch)
                  ? formData.availablePitches.filter((p) => p !== pitch)
                  : [...formData.availablePitches, pitch];
                updateField("availablePitches", pitches);
              }}
              label={pitch}
            />
          ))}
        </div>
      </div>
    </div>
  );

  // Step 2: Points System — hide pointsForLoss
  const POINTS_DEFAULTS = { pointsForWin: "3", pointsForDraw: "1" };

  const renderPointsSystem = () => (
    <div className="space-y-5">
      <div>
        <h5 className="text-base font-medium text-[#FEFCEB] mb-2">Points systems</h5>
        <p className="text-sm text-app-text-secondary">
          Set the points system for any group matches in this stage
        </p>
      </div>

      {Object.entries(POINTS_SYSTEM_OPTIONS)
        .filter(([key]) => key !== "pointsForLoss")
        .map(([key]) => (
          <div key={key} className="space-y-2">
            <label className="block text-[14px] font-normal text-clubbie-white ">
              {POINTS_SYSTEM_TEXTS[key].label}
            </label>
            <input
              type="number"
              min="0"
              className={inputClass}
              placeholder={POINTS_DEFAULTS[key] || "0"}
              value={formData.matchRules ? formData.matchRules[key] : ""}
              onChange={(e) => updateNestedField("matchRules", key, e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </div>
        ))}
    </div>
  );

  // Step 3: Match timing settings
  const renderMatchTiming = () => (
    <div className="space-y-5">
      <div>
        <h5 className="text-base font-medium text-[#FEFCEB] mb-2">Match timing settings</h5>
        <p className="text-sm text-[#7F8585]">
          Set up your timing details for this stage so we can automatically calculate
          total match durations and help you schedule matches effortlessly.
        </p>
      </div>

      {Object.entries(MATCH_RULES_OPTIONS).map(([key, options]) => (
        <div key={key} className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">
            {MATCH_RULES_TEXTS[key].label}
          </label>
          <input
            type="number"
            min="0"
            max="9999"
            className={inputClass}
            placeholder={MATCH_RULES_TEXTS[key].placeholder}
            value={formData.matchRules ? formData.matchRules[key] : ""}
            onChange={(e) =>
              updateNestedField("matchRules", key, e.target.value)
            }
            onWheel={(e) => e.target.blur()}
          />
          <p className="flex items-center gap-1 text-xs text-[#7F8585]">
            <InfoIcon className="w-4 h-4 flex-shrink-0" />
            <span>{MATCH_RULES_TEXTS[key].description}</span>
          </p>
        </div>
      ))}
    </div>
  );

  // Step 4: Match Schedule
  const renderMatchSchedule = () => {
    const handleStartAfterPreviousChange = (checked) => {
      updateField("startAfterPreviousPhase", checked);
      if (checked && previousPhaseEndDate) {
        const matchDaysList = formData.matchSchedule.matchDays || [];
        let nextDay = moment(previousPhaseEndDate).add(1, "day");
        if (matchDaysList.length > 0) {
          let daysChecked = 0;
          while (!matchDaysList.includes(nextDay.format("dddd")) && daysChecked < 7) {
            nextDay.add(1, "day");
            daysChecked++;
          }
        }
        updateNestedField("matchSchedule", "startDate", nextDay.format("YYYY-MM-DD"));
      }
    };

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h5 className="text-base font-medium text-[#FEFCEB]">Match Schedule</h5>
          {(competitionStartDate || competitionEndDate) && (
            <span className="text-sm text-[#7F8585]">
              Competition: {formatDisplayDate(competitionStartDate)} — {formatDisplayDate(competitionEndDate)}
            </span>
          )}
        </div>

        {/* Start After Previous Stage — only for non-first phases */}
        {!isFirstPhase && phases?.length > 0 && (
          <div className="space-y-2">
            <Checkbox
              checked={formData.startAfterPreviousPhase}
              onChange={() => handleStartAfterPreviousChange(!formData.startAfterPreviousPhase)}
              label="Start After Previous Stage"
            />
            <p className="flex items-start gap-2 text-xs text-[#7F8585] mt-1">
              <InfoIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>If checked, this phase will start after the previous phase ends</span>
            </p>
          </div>
        )}

        {/* Start Date */}
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Start Date</label>
          <DatePicker
            value={formData.matchSchedule.startDate ? new Date(formData.matchSchedule.startDate).toISOString().split("T")[0] : ""}
            onChange={(date) => updateNestedField("matchSchedule", "startDate", date)}
            minDate={competitionStartDate}
            maxDate={competitionEndDate}
            disablePastDates={false}
          />
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">End Date</label>
          <DatePicker
            value={formData.matchSchedule.endDate ? new Date(formData.matchSchedule.endDate).toISOString().split("T")[0] : ""}
            onChange={(date) => updateNestedField("matchSchedule", "endDate", date)}
            minDate={formData.matchSchedule.startDate ? new Date(formData.matchSchedule.startDate).toISOString().split("T")[0] : competitionStartDate}
            maxDate={competitionEndDate}
            disablePastDates={false}
          />
        </div>

        {/* Match Days */}
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">
            Match days
          </label>
          <div className="flex flex-wrap gap-2">
            {ALL_DAYS.map((day) => {
              const checked = (formData.matchSchedule.matchDays || []).includes(day);
              return (
                <label
                  key={day}
                  className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                    checked
                      ? "bg-[#00E7FF] text-[#021529]"
                      : "bg-app-card text-[#FEFCEB] hover:bg-[#192634]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = formData.matchSchedule.matchDays || [];
                      const next = current.includes(day)
                        ? current.filter((d) => d !== day)
                        : [...current, day];
                      updateNestedField("matchSchedule", "matchDays", next);
                    }}
                    className="hidden"
                  />
                  {day}
                </label>
              );
            })}
          </div>
        </div>

        {/* Day Start Time */}
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Day Start Time</label>
          <TimePicker
            value={formData.matchSchedule.startTime || "09:00"}
            onChange={(time) => updateNestedField("matchSchedule", "startTime", time)}
          />
        </div>

        {/* Day End Time */}
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Day End Time</label>
          <TimePicker
            value={formData.matchSchedule.dayEndTime || "21:00"}
            onChange={(time) => updateNestedField("matchSchedule", "dayEndTime", time)}
          />
        </div>


        {/* Match Timings — hidden but data preserved */}
      </div>
    );
  };

  // ============================================
  // TEMPLATE-SPECIFIC STEPS
  // ============================================

  // Group Configuration step
  const renderGroupConfig = () => (
    <div className="space-y-5">
      <div>
        <h5 className="text-base font-medium text-[#FEFCEB] mb-2">Group Configuration</h5>
        <p className="text-sm text-[#7F8585]">
          Configure the group stage structure. Groups will be created with empty team slots for you to assign manually.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Group name prefix</label>
        <input
          type="text"
          className={inputClass}
          value={groupConfig.groupNamePrefix}
          placeholder="Group "
          onChange={(e) => setGroupConfig({ ...groupConfig, groupNamePrefix: e.target.value })}
        />
        <p className="flex items-center gap-1 text-xs text-[#7F8585]">
          <InfoIcon className="w-4 h-4 flex-shrink-0" />
          <span>Groups will be named: {groupConfig.groupNamePrefix}1, {groupConfig.groupNamePrefix}2, etc.</span>
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Number of groups</label>
        <input
          type="number"
          min="1"
          max="32"
          className={inputClass}
          value={groupConfig.numGroups}
          placeholder="2"
          onChange={(e) => setGroupConfig({ ...groupConfig, numGroups: e.target.value })}
          onWheel={(e) => e.target.blur()}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Teams per group</label>
        <input
          type="number"
          min="2"
          max="64"
          className={inputClass}
          value={groupConfig.teamsPerGroup}
          placeholder="4"
          onChange={(e) => setGroupConfig({ ...groupConfig, teamsPerGroup: e.target.value })}
          onWheel={(e) => e.target.blur()}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Times teams face each other</label>
        <input
          type="number"
          min="1"
          max="4"
          className={inputClass}
          value={groupConfig.timesToFaceEachOther}
          placeholder="1"
          onChange={(e) => setGroupConfig({ ...groupConfig, timesToFaceEachOther: e.target.value })}
          onWheel={(e) => e.target.blur()}
        />
        <p className="flex items-center gap-1 text-xs text-[#7F8585]">
          <InfoIcon className="w-4 h-4 flex-shrink-0" />
          <span>1 = Single round-robin, 2 = Home & away, etc.</span>
        </p>
      </div>
    </div>
  );

  // Bracket Configuration step
  const renderBracketConfig = () => (
    <div className="space-y-5">
      <div>
        <h5 className="text-base font-medium text-[#FEFCEB] mb-2">Bracket Configuration</h5>
        <p className="text-sm text-[#7F8585]">
          Configure the knockout bracket. Team slots will be created empty for you to assign manually.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Bracket name</label>
        <input
          type="text"
          className={inputClass}
          value={bracketConfig.bracketName}
          placeholder="Main Bracket"
          onChange={(e) => setBracketConfig({ ...bracketConfig, bracketName: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Number of teams</label>
        <div className="relative">
          <select
            className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors appearance-none cursor-pointer"
            value={bracketConfig.teamsCount}
            onChange={(e) => setBracketConfig({ ...bracketConfig, teamsCount: e.target.value })}
          >
            {TEAMS_COUNT_OPTIONS.map((count) => (
              <option key={count} value={count}>{count}</option>
            ))}
            <option value="custom">Custom</option>
          </select>
          <svg
            className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-app-text-secondary"
            width="16"
            height="16"
            viewBox="0 0 12 8"
            fill="none"
          >
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="flex items-center gap-1 text-xs text-[#7F8585]">
          <InfoIcon className="w-4 h-4 flex-shrink-0" />
          <span>Preset values are powers of 2. Use "Custom" for any team count.</span>
        </p>
      </div>

      {bracketConfig.teamsCount === "custom" && (
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Enter custom number of teams</label>
          <input
            type="number"
            min="2"
            className={inputClass}
            placeholder="Enter a number"
            value={bracketConfig.customTeamsCount || ""}
            onChange={(e) => setBracketConfig({ ...bracketConfig, customTeamsCount: parseInt(e.target.value) })}
            onWheel={(e) => e.target.blur()}
          />
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Legs per round</label>
        <input
          type="number"
          min="1"
          max="4"
          className={inputClass}
          value={bracketConfig.numberOfLegs}
          placeholder="1"
          onChange={(e) => setBracketConfig({ ...bracketConfig, numberOfLegs: e.target.value })}
          onWheel={(e) => e.target.blur()}
        />
        <p className="flex items-center gap-1 text-xs text-[#7F8585]">
          <InfoIcon className="w-4 h-4 flex-shrink-0" />
          <span>1 = Single match per round, 2 = Home & away. The final is always a single leg.</span>
        </p>
      </div>
    </div>
  );

  // Knockout Phase Settings step (for Group+Knockout only)
  const renderKnockoutPhaseSettings = () => (
    <div className="space-y-5">
      <div>
        <h5 className="text-base font-medium text-[#FEFCEB] mb-2">Knockout Phase Settings</h5>
        <p className="text-sm text-[#7F8585]">
          Configure the knockout phase that follows the group stage.
        </p>
      </div>

      {/* Phase name — always shown */}
      <div className="space-y-2">
        <label className="block text-[14px] font-normal text-clubbie-white ">Knockout phase name</label>
        <input
          type="text"
          className={inputClass}
          value={phase2Settings.name}
          placeholder="Knockout Stage"
          onChange={(e) => setPhase2Settings({ ...phase2Settings, name: e.target.value })}
        />
      </div>

      {/* Inherit pitches, points & timing from group phase */}
      <Checkbox
        checked={phase2Settings.inheritSettings}
        onChange={() => setPhase2Settings({ ...phase2Settings, inheritSettings: !phase2Settings.inheritSettings })}
        label="Copy pitches, points & timing from group stage"
      />
      <p className="flex items-start gap-2 text-xs text-[#7F8585]">
        <InfoIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>If checked, the knockout phase will reuse the same pitches, points system, and match timing from the group phase. The schedule is always set separately.</span>
      </p>

      {/* Pitches, points & timing — only when NOT inheriting */}
      {!phase2Settings.inheritSettings && (
        <div className="space-y-5 border-t border-app-surface pt-5">
          <p className="text-sm text-[#7F8585]">
            Customize pitches, points, and timing for the knockout phase.
          </p>

          {/* Pitches */}
          <div className="space-y-2">
            <label className="block text-[14px] font-normal text-clubbie-white ">Available pitches</label>
            <div className="flex flex-col space-y-2">
              {getPitchOptions().map((pitch) => (
                <Checkbox
                  key={pitch}
                  checked={(phase2Settings.availablePitches || []).includes(pitch)}
                  onChange={() => {
                    const current = phase2Settings.availablePitches || [];
                    const pitches = current.includes(pitch)
                      ? current.filter((p) => p !== pitch)
                      : [...current, pitch];
                    setPhase2Settings({ ...phase2Settings, availablePitches: pitches });
                  }}
                  label={pitch}
                />
              ))}
            </div>
          </div>

          {/* Points */}
          {Object.entries(POINTS_SYSTEM_OPTIONS)
            .filter(([key]) => key !== "pointsForLoss")
            .map(([key]) => (
              <div key={key} className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">
                  {POINTS_SYSTEM_TEXTS[key].label}
                </label>
                <input
                  type="number"
                  min="0"
                  className={inputClass}
                  value={phase2Settings.matchRules?.[key] || ""}
                  onChange={(e) => setPhase2Settings({
                    ...phase2Settings,
                    matchRules: { ...(phase2Settings.matchRules || {}), [key]: e.target.value },
                  })}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
            ))}

          {/* Match timing */}
          {Object.entries(MATCH_RULES_OPTIONS).map(([key]) => (
            <div key={key} className="space-y-2">
              <label className="block text-[14px] font-normal text-clubbie-white ">
                {MATCH_RULES_TEXTS[key].label}
              </label>
              <input
                type="number"
                min="0"
                max="9999"
                className={inputClass}
                placeholder={MATCH_RULES_TEXTS[key].placeholder}
                value={phase2Settings.matchRules?.[key] || ""}
                onChange={(e) => setPhase2Settings({
                  ...phase2Settings,
                  matchRules: { ...(phase2Settings.matchRules || {}), [key]: e.target.value },
                })}
                onWheel={(e) => e.target.blur()}
              />
            </div>
          ))}
        </div>
      )}

      {/* Schedule — ALWAYS shown, since knockout schedule will differ from group */}
      <div className="space-y-5 border-t border-app-surface pt-5">
        <h5 className="text-base font-medium text-[#FEFCEB]">Knockout Schedule</h5>

        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Start Date</label>
          <DatePicker
            value={phase2Settings.matchSchedule?.startDate || ""}
            onChange={(date) => setPhase2Settings({
              ...phase2Settings,
              matchSchedule: { ...(phase2Settings.matchSchedule || {}), startDate: date },
            })}
            minDate={competitionStartDate}
            maxDate={competitionEndDate}
            disablePastDates={false}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">End Date</label>
          <DatePicker
            value={phase2Settings.matchSchedule?.endDate || ""}
            onChange={(date) => setPhase2Settings({
              ...phase2Settings,
              matchSchedule: { ...(phase2Settings.matchSchedule || {}), endDate: date },
            })}
            minDate={phase2Settings.matchSchedule?.startDate || competitionStartDate}
            maxDate={competitionEndDate}
            disablePastDates={false}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Day Start Time</label>
          <TimePicker
            value={phase2Settings.matchSchedule?.startTime || "09:00"}
            onChange={(time) => setPhase2Settings({
              ...phase2Settings,
              matchSchedule: { ...(phase2Settings.matchSchedule || {}), startTime: time },
            })}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-[14px] font-normal text-clubbie-white ">Day End Time</label>
          <TimePicker
            value={phase2Settings.matchSchedule?.dayEndTime || "21:00"}
            onChange={(time) => setPhase2Settings({
              ...phase2Settings,
              matchSchedule: { ...(phase2Settings.matchSchedule || {}), dayEndTime: time },
            })}
          />
        </div>
      </div>
    </div>
  );

  // ============================================
  // MODAL TITLE
  // ============================================

  const getModalTitle = () => {
    if (mode === "edit") return "Edit stage";
    const titles = {
      manualGrid: "Create stage",
      knockoutPhase: "Create knockout stage",
      groupPhase: "Create group stage",
      groupStageKnockout: "Create group + knockout",
    };
    return titles[templateType] || "Create stage";
  };

  const renderFooter = () => {
    // Edit mode: single-step, just Save
    if (mode === "edit") {
      return (
        <div className="flex justify-end gap-3 w-full">
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isLoading}
            className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="small" />
                <span>Updating...</span>
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 w-full">
        <button
          type="button"
          className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
          onClick={handleClose}
        >
          Cancel
        </button>

        <div className="ml-auto flex items-center gap-3">
          {currentStep > 1 && (
            <button
              type="button"
              className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Back
            </button>
          )}

          {currentStep < totalSteps ? (
            <button
              type="button"
              className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer"
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreate}
              disabled={isLoading}
              className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="small" />
                  <span>Creating...</span>
                </>
              ) : (
                "Create"
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <ClubbieModal
      isOpen={isOpen}
      width="720px"
      height={mode === "edit" ? undefined : "75vh"}
      maxHeight={mode === "edit" ? undefined : "80vh"}
      toggle={handleClose}
      title={mode === "edit" ? "Edit points system" : getModalTitle()}
      footer={renderFooter()}
    >
      {mode === "edit" ? (
        renderPointsSystem()
      ) : (
        <>
          {renderStepHeader()}

          {/* Base steps (shared by all templates) */}
          {currentStep === 1 && renderGeneralSettings()}
          {currentStep === 2 && renderPointsSystem()}
          {currentStep === 3 && renderMatchTiming()}
          {currentStep === 4 && renderMatchSchedule()}

          {/* Knockout: step 5 = bracket config */}
          {currentStep === 5 && templateType === "knockoutPhase" && renderBracketConfig()}

          {/* Group: step 5 = group config */}
          {currentStep === 5 && templateType === "groupPhase" && renderGroupConfig()}

          {/* Group+Knockout: step 5 = group config, step 6 = knockout settings, step 7 = bracket config */}
          {currentStep === 5 && templateType === "groupStageKnockout" && renderGroupConfig()}
          {currentStep === 6 && templateType === "groupStageKnockout" && renderKnockoutPhaseSettings()}
          {currentStep === 7 && templateType === "groupStageKnockout" && renderBracketConfig()}
        </>
      )}
    </ClubbieModal>
  );
};

export default PhaseSettingsModal;
